import React, { memo, useState, useCallback, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Search, 
  Filter, 
  SortAsc, 
  SortDesc,
  Grid3X3,
  List,
  RefreshCw,
  X,
  Building2,
  MapPin,
  Calendar,
  Shield,
  Star,
  Users,
  Package,
  AlertCircle,
  Loader2,
  CheckCircle,
  Globe,
  Award
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";

// Enhanced Supplier interface
interface Supplier {
  id: string;
  business_name: string;
  slug: string;
  logo_url?: string;
  description?: string;
  website?: string;
  location?: string;
  address?: string;
  establishment_year?: number;
  nature_of_business?: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  trust_seal_verified: boolean;
  machinesearch_certification: boolean;
  rating?: number;
  review_count?: number;
  product_count?: number;
  created_at: string;
  updated_at: string;
  accounts?: {
    full_name: string;
    email: string;
    phone?: string;
  };
}

// Filter interface
interface SupplierFilters {
  search: string;
  verificationStatus: 'all' | 'verified' | 'pending' | 'rejected';
  location: string;
  businessType: string;
  certification: 'all' | 'trust_verified' | 'machinesearch_certified';
  sortBy: 'name' | 'rating' | 'products' | 'established' | 'newest';
  sortOrder: 'asc' | 'desc';
}

// Supplier Card Component
const SupplierCard = memo<{
  supplier: Supplier;
  viewMode: 'grid' | 'list';
  onClick?: (supplier: Supplier) => void;
  className?: string;
}>(({ supplier, viewMode, onClick, className = '' }) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleCardClick = useCallback(() => {
    onClick?.(supplier);
  }, [onClick, supplier]);

  const formatEstablishmentYear = useCallback((year?: number) => {
    if (!year) return 'N/A';
    return `Est. ${year}`;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short'
    });
  }, []);

  const getVerificationColor = useCallback((status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }, []);

  if (viewMode === 'list') {
    return (
      <Card 
        className={`hover:shadow-md transition-all duration-200 cursor-pointer ${className}`}
        onClick={handleCardClick}
      >
        <div className="flex flex-col md:flex-row">
          {/* Logo Section */}
          <div className="relative w-full md:w-32 h-32 flex-shrink-0">
            {!imageError && supplier.logo_url ? (
              <img 
                src={supplier.logo_url} 
                alt={supplier.business_name}
                className="w-full h-full object-cover rounded-t-lg md:rounded-l-lg md:rounded-t-none"
                loading="lazy"
                onError={handleImageError}
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-t-lg md:rounded-l-lg md:rounded-t-none">
                <Building2 className="h-8 w-8 text-gray-400" />
              </div>
            )}
            
            {/* Verification Badges */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {supplier.machinesearch_certification && (
                <Badge variant="default" className="text-xs bg-blue-600">
                  <Award className="h-3 w-3 mr-1" />
                  Certified
                </Badge>
              )}
              {supplier.trust_seal_verified && (
                <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                  <Shield className="h-3 w-3 mr-1" />
                  Trusted
                </Badge>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 p-4 min-w-0">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold line-clamp-1 mb-1" title={supplier.business_name}>
                  {supplier.business_name}
                </h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                  <Badge className={getVerificationColor(supplier.verification_status)}>
                    {supplier.verification_status}
                  </Badge>
                  {supplier.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{supplier.location}</span>
                    </div>
                  )}
                  {supplier.establishment_year && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatEstablishmentYear(supplier.establishment_year)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {supplier.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {supplier.description}
              </p>
            )}

            {/* Stats and Details */}
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-3">
              {supplier.nature_of_business && (
                <span className="font-medium">{supplier.nature_of_business}</span>
              )}
              {supplier.product_count !== undefined && (
                <div className="flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  <span>{supplier.product_count} products</span>
                </div>
              )}
              {supplier.website && (
                <div className="flex items-center gap-1 text-blue-600">
                  <Globe className="h-3 w-3" />
                  <span>Website</span>
                </div>
              )}
            </div>

            {/* Rating and Join Date */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {supplier.rating && supplier.rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-medium">{supplier.rating.toFixed(1)}</span>
                    {supplier.review_count && (
                      <span className="text-xs text-muted-foreground">
                        ({supplier.review_count} reviews)
                      </span>
                    )}
                  </div>
                )}
                <span className="text-xs text-muted-foreground">
                  Joined {formatDate(supplier.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Grid view
  return (
    <Card 
      className={`overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group ${className}`}
      onClick={handleCardClick}
    >
      {/* Logo Section */}
      <div className="relative h-32 overflow-hidden">
        {!imageError && supplier.logo_url ? (
          <img 
            src={supplier.logo_url} 
            alt={supplier.business_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-gray-400" />
          </div>
        )}
        
        {/* Verification Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {supplier.machinesearch_certification && (
            <Badge variant="default" className="text-xs bg-blue-600">
              <Award className="h-3 w-3 mr-1" />
              Certified
            </Badge>
          )}
          {supplier.trust_seal_verified && (
            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
              <Shield className="h-3 w-3 mr-1" />
              Trusted
            </Badge>
          )}
        </div>

        {/* Verification Status */}
        <div className="absolute top-2 right-2">
          <Badge className={getVerificationColor(supplier.verification_status)}>
            {supplier.verification_status}
          </Badge>
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="p-4">
        {/* Header */}
        <div className="mb-3">
          <h3 className="font-semibold text-lg line-clamp-2 mb-2" title={supplier.business_name}>
            {supplier.business_name}
          </h3>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            {supplier.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="truncate">{supplier.location}</span>
              </div>
            )}
            {supplier.establishment_year && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatEstablishmentYear(supplier.establishment_year)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {supplier.description && (
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
            {supplier.description}
          </p>
        )}

        {/* Business Type */}
        {supplier.nature_of_business && (
          <div className="mb-3">
            <Badge variant="outline" className="text-xs">
              {supplier.nature_of_business}
            </Badge>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            {supplier.product_count !== undefined && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Package className="h-3 w-3" />
                <span>{supplier.product_count}</span>
              </div>
            )}
            {supplier.website && (
              <div className="flex items-center gap-1 text-blue-600">
                <Globe className="h-3 w-3" />
              </div>
            )}
          </div>
          
          {supplier.rating && supplier.rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{supplier.rating.toFixed(1)}</span>
              {supplier.review_count && (
                <span className="text-xs text-muted-foreground">
                  ({supplier.review_count})
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

SupplierCard.displayName = 'SupplierCard';

// Filter panel component
const FilterPanel = memo<{
  filters: SupplierFilters;
  onFiltersChange: (filters: Partial<SupplierFilters>) => void;
  availableLocations: string[];
  availableBusinessTypes: string[];
  className?: string;
}>(({ filters, onFiltersChange, availableLocations, availableBusinessTypes, className }) => {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filters
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Verification Status Filter */}
        <div>
          <h4 className="font-medium mb-3">Verification Status</h4>
          <Select 
            value={filters.verificationStatus} 
            onValueChange={(value) => onFiltersChange({ verificationStatus: value as any })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Location Filter */}
        <div>
          <h4 className="font-medium mb-3">Location</h4>
          <Select 
            value={filters.location} 
            onValueChange={(value) => onFiltersChange({ location: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {availableLocations.map(location => (
                <SelectItem key={location} value={location}>
                  {location}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Business Type Filter */}
        <div>
          <h4 className="font-medium mb-3">Business Type</h4>
          <Select 
            value={filters.businessType} 
            onValueChange={(value) => onFiltersChange({ businessType: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {availableBusinessTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Certification Filter */}
        <div>
          <h4 className="font-medium mb-3">Certifications</h4>
          <Select 
            value={filters.certification} 
            onValueChange={(value) => onFiltersChange({ certification: value as any })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All certifications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              <SelectItem value="machinesearch_certified">MachineSearch Certified</SelectItem>
              <SelectItem value="trust_verified">Trust Verified</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
});

FilterPanel.displayName = 'FilterPanel';

// Skeleton loading component
const SuppliersListingSkeleton = memo<{ viewMode: 'grid' | 'list'; count?: number }>(({ viewMode, count = 12 }) => (
  <div className={viewMode === 'grid' 
    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    : "space-y-4"
  }>
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="overflow-hidden">
        {viewMode === 'grid' ? (
          <>
            <Skeleton className="h-32 w-full" />
            <div className="p-4 space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/6" />
              </div>
            </div>
          </>
        ) : (
          <div className="flex">
            <Skeleton className="h-32 w-32 flex-shrink-0" />
            <div className="flex-1 p-4 space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/6" />
              </div>
            </div>
          </div>
        )}
      </Card>
    ))}
  </div>
));

SuppliersListingSkeleton.displayName = 'SuppliersListingSkeleton';

// Main component
const SuppliersDirectoryPage = memo(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize filters from URL params
  const initialFilters = useMemo(() => ({
    search: searchParams.get('search') || '',
    verificationStatus: (searchParams.get('status') as any) || 'all',
    location: searchParams.get('location') || 'all',
    businessType: searchParams.get('type') || 'all',
    certification: (searchParams.get('certification') as any) || 'all',
    sortBy: (searchParams.get('sortBy') as any) || 'name',
    sortOrder: (searchParams.get('sortOrder') as any) || 'asc'
  }), [searchParams]);
  
  const [filters, setFilters] = useState<SupplierFilters>(initialFilters);
  
  // Load suppliers
  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('supplier_profiles')
        .select(`
          *,
          accounts (
            full_name,
            email,
            phone
          )
        `)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Transform data and add computed fields
      const transformedSuppliers: Supplier[] = (data || []).map(supplier => ({
        ...supplier,
        slug: supplier.slug || supplier.business_name?.toLowerCase().replace(/\s+/g, '-') || supplier.id,
        product_count: 0, // TODO: Add actual product count from products table
        rating: supplier.rating || 0,
        review_count: supplier.review_count || 0
      }));
      
      setSuppliers(transformedSuppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load suppliers on mount
  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);
  
  // Filter suppliers
  const filteredSuppliers = useMemo(() => {
    let result = [...suppliers];
    
    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(supplier => 
        supplier.business_name.toLowerCase().includes(searchTerm) ||
        supplier.description?.toLowerCase().includes(searchTerm) ||
        supplier.location?.toLowerCase().includes(searchTerm) ||
        supplier.nature_of_business?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply verification status filter
    if (filters.verificationStatus !== 'all') {
      result = result.filter(supplier => supplier.verification_status === filters.verificationStatus);
    }
    
    // Apply location filter
    if (filters.location !== 'all') {
      result = result.filter(supplier => supplier.location === filters.location);
    }
    
    // Apply business type filter
    if (filters.businessType !== 'all') {
      result = result.filter(supplier => supplier.nature_of_business === filters.businessType);
    }
    
    // Apply certification filter
    if (filters.certification !== 'all') {
      if (filters.certification === 'machinesearch_certified') {
        result = result.filter(supplier => supplier.machinesearch_certification);
      } else if (filters.certification === 'trust_verified') {
        result = result.filter(supplier => supplier.trust_seal_verified);
      }
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (filters.sortBy) {
        case 'name':
          aValue = a.business_name.toLowerCase();
          bValue = b.business_name.toLowerCase();
          break;
        case 'rating':
          aValue = a.rating || 0;
          bValue = b.rating || 0;
          break;
        case 'products':
          aValue = a.product_count || 0;
          bValue = b.product_count || 0;
          break;
        case 'established':
          aValue = a.establishment_year || 0;
          bValue = b.establishment_year || 0;
          break;
        case 'newest':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = a.business_name.toLowerCase();
          bValue = b.business_name.toLowerCase();
      }
      
      if (filters.sortOrder === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });
    
    return result;
  }, [suppliers, filters]);
  
  // Get available filter options
  const availableOptions = useMemo(() => {
    const locations = [...new Set(suppliers.map(s => s.location).filter((location): location is string => Boolean(location)))].sort();
    const businessTypes = [...new Set(suppliers.map(s => s.nature_of_business).filter((type): type is string => Boolean(type)))].sort();
    
    return { locations, businessTypes };
  }, [suppliers]);
  
  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.search) params.set('search', filters.search);
    if (filters.verificationStatus !== 'all') params.set('status', filters.verificationStatus);
    if (filters.location !== 'all') params.set('location', filters.location);
    if (filters.businessType !== 'all') params.set('type', filters.businessType);
    if (filters.certification !== 'all') params.set('certification', filters.certification);
    if (filters.sortBy !== 'name') params.set('sortBy', filters.sortBy);
    if (filters.sortOrder !== 'asc') params.set('sortOrder', filters.sortOrder);
    
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);
  
  // Handle supplier card click
  const handleSupplierClick = useCallback((supplier: Supplier) => {
    window.location.href = `/suppliers/${supplier.slug}`;
  }, []);
  
  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: Partial<SupplierFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);
  
  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      verificationStatus: 'all',
      location: 'all',
      businessType: 'all',
      certification: 'all',
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }, []);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => 
    filters.search ||
    filters.verificationStatus !== 'all' ||
    filters.location !== 'all' ||
    filters.businessType !== 'all' ||
    filters.certification !== 'all' ||
    filters.sortBy !== 'name' ||
    filters.sortOrder !== 'asc',
    [filters]
  );
  
  // Breadcrumb items
  const breadcrumbItems = useMemo(() => [
    { name: "Home", href: "/" },
    { name: "Suppliers", href: "/suppliers" }
  ], []);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation items={breadcrumbItems} />
      
      {/* Header */}
      <div className="mt-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Suppliers Directory
            </h1>
            <p className="text-gray-600 mt-2">
              Discover verified suppliers and manufacturers for your business needs
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadSuppliers}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Filters Sidebar */}
        <div className={`lg:w-80 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            availableLocations={availableOptions.locations}
            availableBusinessTypes={availableOptions.businessTypes}
            className="sticky top-4"
          />
        </div>
        
        {/* Main Content */}
        <div className="flex-1">
          {/* Search and Sort Bar */}
          <div className="mb-6 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search suppliers..."
                value={filters.search}
                onChange={(e) => handleFiltersChange({ search: e.target.value })}
                className="pl-10 pr-4"
              />
            </div>
            
            {/* Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 text-xs">
                      !
                    </Badge>
                  )}
                </Button>
                
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Select 
                  value={filters.sortBy} 
                  onValueChange={(value) => handleFiltersChange({ sortBy: value as any })}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="rating">Rating</SelectItem>
                    <SelectItem value="products">Products</SelectItem>
                    <SelectItem value="established">Established</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFiltersChange({ 
                    sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' 
                  })}
                  className="p-2"
                >
                  {filters.sortOrder === 'asc' ? 
                    <SortAsc className="h-4 w-4" /> : 
                    <SortDesc className="h-4 w-4" />
                  }
                </Button>
              </div>
            </div>
            
            {/* Results Summary */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                {loading ? 'Loading...' : 
                  `${filteredSuppliers.length} supplier${filteredSuppliers.length !== 1 ? 's' : ''} found`
                }
              </span>
              
              {filteredSuppliers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {suppliers.filter(s => s.verification_status === 'verified').length} verified
                  </Badge>
                  <Badge variant="outline">
                    {suppliers.filter(s => s.machinesearch_certification).length} certified
                  </Badge>
                </div>
              )}
            </div>
          </div>
          
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={loadSuppliers}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Content */}
          {loading ? (
            <SuppliersListingSkeleton viewMode={viewMode} />
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-gray-50">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {hasActiveFilters ? 'No suppliers match your criteria' : 'No suppliers found'}
              </h3>
              <p className="text-gray-500 mb-4 max-w-md mx-auto">
                {hasActiveFilters 
                  ? 'Try adjusting your filters to find more suppliers.'
                  : 'There are no suppliers available at the moment.'
                }
              </p>
              {hasActiveFilters && (
                <Button onClick={resetFilters}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset Filters
                </Button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              : "space-y-4"
            }>
              {filteredSuppliers.map((supplier) => (
                <SupplierCard
                  key={supplier.id}
                  supplier={supplier}
                  viewMode={viewMode}
                  onClick={handleSupplierClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

SuppliersDirectoryPage.displayName = 'SuppliersDirectoryPage';

export default SuppliersDirectoryPage; 